
import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { FeedbackRequest } from './useFeedbackRequests';
import { RequestCardStatus } from './card/RequestCardStatus';
import { RequestCardParty } from './card/RequestCardParty';
import { RequestCardMessage } from './card/RequestCardMessage';
import { RequestCardActions } from './card/RequestCardActions';
import { useFeedbackActions } from './card/useFeedbackActions';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Folder, Send, Inbox } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type FeedbackRequestCardProps = {
  request: FeedbackRequest;
  type: 'sent' | 'received';
  onActionPerformed?: () => void;
};

const FeedbackRequestCard = ({ request, type, onActionPerformed }: FeedbackRequestCardProps) => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isEntreprise = profile?.role === 'entreprise';
  const isCandidat = profile?.role === 'candidat';
  const [currentStatus, setCurrentStatus] = useState(request.status);
  const { feedbackId, handleRespondToRequest, handleViewFeedback } = useFeedbackActions(request);
  const [lastCheckTime, setLastCheckTime] = useState<number>(0);
  const [projectInfo, setProjectInfo] = useState<{name: string, id: string} | null>(null);
  const [lastReminderSent, setLastReminderSent] = useState<string | null>(request.last_reminder_sent || null);
  const [isReminding, setIsReminding] = useState(false);

  // Vérifier si la demande est associée à un projet
  useEffect(() => {
    if (!request.id) return;
    
    const checkProjectAssociation = async () => {
      try {
        const { data, error } = await supabase
          .from('projects_requests')
          .select(`
            project_id,
            projects (name)
          `)
          .eq('request_id', request.id)
          .maybeSingle();
        
        if (error) {
          console.error('Error checking project association:', error);
          return;
        }
        
        if (data && data.project_id && data.projects) {
          setProjectInfo({
            id: data.project_id,
            name: data.projects.name
          });
        }
      } catch (error) {
        console.error('Error in checkProjectAssociation:', error);
      }
    };
    
    checkProjectAssociation();
  }, [request.id]);

  // Fonction optimisée de vérification du statut avec une meilleure gestion des types
  const checkRequestStatus = useCallback(async () => {
    if (!request.id) return;
    
    const now = Date.now();
    // Vérifier le statut au maximum toutes les 10 secondes
    if (now - lastCheckTime < 10000) {
      return;
    }
    
    setLastCheckTime(now);
    
    try {
      // Déterminer la table à interroger en fonction du type de demande
      const tableToUse = (request.id.includes('-c2r-') || 
                         (isCandidat && type === 'sent') || 
                         (isEntreprise && type === 'received')) 
                       ? 'candidate_to_recruiter_feedback_requests' 
                       : 'feedback_requests';
      
      // Utiliser une fonction commune pour traiter les résultats des deux tables
      const processQueryResult = (data: any, error: any) => {
        if (error) {
          console.error(`Error checking request status in ${tableToUse}:`, error);
          return;
        }
        
        // Sécuriser l'accès aux propriétés avec vérification complète
        if (data && typeof data === 'object' && 'status' in data) {
          const newStatus = data.status as string;
          if (newStatus !== currentStatus) {
            console.log(`Status changed for request ${request.id}: ${currentStatus} -> ${newStatus}`);
            setCurrentStatus(newStatus);
            
            // Notifier le parent du changement de statut
            if (onActionPerformed) {
              onActionPerformed();
            }
          }
          
          // Mettre à jour la dernière date de relance si disponible
          if ('last_reminder_sent' in data && data.last_reminder_sent !== lastReminderSent) {
            setLastReminderSent(data.last_reminder_sent);
          }
        }
      };
      
      // Exécuter la requête appropriée selon la table
      if (tableToUse === 'candidate_to_recruiter_feedback_requests') {
        const { data, error } = await supabase
          .from('candidate_to_recruiter_feedback_requests' as any)
          .select('status, feedback_id, last_reminder_sent')
          .eq('id', request.id)
          .maybeSingle();
          
        processQueryResult(data, error);
      } else {
        const { data, error } = await supabase
          .from('feedback_requests')
          .select('status, feedback_id, last_reminder_sent')
          .eq('id', request.id)
          .maybeSingle();
          
        processQueryResult(data, error);
      }
    } catch (error) {
      console.error('Error in checkRequestStatus:', error);
      toast({
        title: "Erreur",
        description: "Impossible de vérifier le statut de la demande",
        variant: "destructive",
      });
    }
  }, [request.id, currentStatus, onActionPerformed, lastCheckTime, isCandidat, isEntreprise, type, toast, lastReminderSent]);

  // Initialiser le statut et vérifier périodiquement
  useEffect(() => {
    // Initialize with request status
    setCurrentStatus(request.status);
    
    // Check status on mount
    checkRequestStatus();
    
    // Set up less frequent checks (10 seconds instead of 5)
    const statusInterval = setInterval(checkRequestStatus, 10000);
    
    return () => clearInterval(statusInterval);
  }, [request.id, request.status, checkRequestStatus]);

  const handleRespond = () => {
    handleRespondToRequest();
    if (onActionPerformed) onActionPerformed();
  };

  const handleView = async () => {
    await handleViewFeedback();
    if (onActionPerformed) onActionPerformed();
  };

  const handleViewProject = () => {
    if (projectInfo) {
      navigate(`/projects/${projectInfo.id}`);
    }
  };

  const handleSendReminder = async () => {
    if (isReminding || !request.id) return;
    
    // Vérifier si une relance a été envoyée dans les dernières 24 heures
    if (lastReminderSent) {
      const lastSentTime = new Date(lastReminderSent).getTime();
      const now = new Date().getTime();
      const hoursSinceLastReminder = (now - lastSentTime) / (1000 * 60 * 60);
      
      if (hoursSinceLastReminder < 24) {
        toast({
          title: "Relance impossible",
          description: "Vous ne pouvez envoyer une relance qu'une fois toutes les 24 heures.",
          variant: "destructive",
        });
        return;
      }
    }
    
    setIsReminding(true);
    
    try {
      // Déterminer la table à utiliser
      const tableToUse = request.id.includes('-c2r-') 
        ? 'candidate_to_recruiter_feedback_requests' 
        : 'feedback_requests';
      
      // Mettre à jour la date de dernière relance
      const now = new Date().toISOString();
      const { error: updateError } = await supabase
        .from(tableToUse)
        .update({ last_reminder_sent: now })
        .eq('id', request.id);
      
      if (updateError) {
        throw updateError;
      }
      
      // Appeler l'edge function pour envoyer l'email de relance
      const { error: functionError } = await supabase.functions.invoke('send-feedback-request', {
        body: { 
          requestId: request.id, 
          isReminder: true 
        }
      });
      
      if (functionError) {
        throw functionError;
      }
      
      // Mettre à jour l'état local
      setLastReminderSent(now);
      
      toast({
        title: "Relance envoyée",
        description: "Un e-mail de rappel a été envoyé au destinataire.",
      });
      
      if (onActionPerformed) {
        onActionPerformed();
      }
    } catch (error: any) {
      console.error('Error sending reminder:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer la relance: " + (error.message || "Erreur inconnue"),
        variant: "destructive",
      });
    } finally {
      setIsReminding(false);
    }
  };

  // Déterminer si le bouton de relance doit être désactivé
  const isReminderDisabled = lastReminderSent ? 
    (new Date().getTime() - new Date(lastReminderSent).getTime() < 24 * 60 * 60 * 1000) : 
    false;

  // Ajouter un badge pour distinguer si c'est envoyé ou reçu
  const directionBadge = type === 'sent' ? (
    <Badge variant="outline" className="flex items-center gap-1 bg-green-50 text-green-700 border-green-200">
      <Send className="h-3 w-3" />
      <span>Envoyé</span>
    </Badge>
  ) : (
    <Badge variant="outline" className="flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200">
      <Inbox className="h-3 w-3" />
      <span>Reçu</span>
    </Badge>
  );

  return (
    <Card key={request.id} className="overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors">
      <div className="border-l-4 border-brandBlue p-4 flex flex-col md:flex-row justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <RequestCardStatus status={currentStatus} createdAt={request.created_at} />
            {directionBadge}
          </div>
          
          <h3 className="font-semibold text-lg mb-1">
            {request.job_title}
          </h3>
          
          {projectInfo && (
            <div className="flex items-center mb-2">
              <Badge 
                variant="outline" 
                className="flex items-center gap-1 cursor-pointer hover:bg-gray-100"
                onClick={handleViewProject}
              >
                <Folder className="h-3 w-3" />
                <span>Projet: {projectInfo.name}</span>
              </Badge>
            </div>
          )}
          
          <RequestCardParty 
            type={type}
            isCandidat={isCandidat}
            recruiterName={request.recruiter_name}
            recruiterEmail={request.recruiter_email}
            recruiterCompany={request.recruiter_company}
            candidateName={request.candidate_name}
            candidateEmail={request.candidate_email}
          />
          
          <RequestCardMessage message={request.message} />
          
          {lastReminderSent && type === 'sent' && (
            <div className="mt-2 text-xs text-gray-500">
              Dernière relance: {new Date(lastReminderSent).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>
        
        <RequestCardActions 
          type={type}
          status={currentStatus}
          feedbackId={feedbackId}
          onRespond={handleRespond}
          onViewFeedback={handleView}
          onSendReminder={type === 'sent' ? handleSendReminder : undefined}
          disableReminder={isReminderDisabled}
          isReminding={isReminding}
        />
      </div>
    </Card>
  );
};

export default FeedbackRequestCard;
